export const LOCAL_ENTRA_REDIRECT_URI = 'http://localhost:4173';

import type { AuthRole } from './model.js';

export interface EntraBrowserAuthConfig {
  clientId: string;
  tenantId: string;
  redirectUri: string;
  scopes: readonly ['User.Read'];
}

export type RuntimeAuthConfig =
  | { mode: 'ENTRA'; entra: EntraBrowserAuthConfig }
  | { mode: 'MOCK'; mockRole: AuthRole };

export interface RuntimeConfigSource {
  authMode?: unknown;
  entraClientId?: unknown;
  entraTenantId?: unknown;
  entraRedirectUri?: unknown;
  mockRole?: unknown;
}

export function parseRuntimeAuthConfig(source: RuntimeConfigSource): RuntimeAuthConfig {
  const mode = readString(source.authMode).toLowerCase();
  if (mode === 'mock') {
    const requestedRole = readString(source.mockRole).toUpperCase();
    return {
      mode: 'MOCK',
      mockRole: ['OWNER', 'ADMIN', 'OPERATOR', 'VIEWER'].includes(requestedRole)
        ? requestedRole as AuthRole
        : 'ADMIN',
    };
  }

  const clientId = requireUuid(source.entraClientId, 'Microsoft Entra client ID');
  const tenantId = requireUuid(source.entraTenantId, 'Microsoft Entra tenant ID');
  const redirectUri = validSpaRedirectUri(readString(source.entraRedirectUri));

  return {
    mode: 'ENTRA',
    entra: {
      clientId,
      tenantId,
      redirectUri,
      scopes: ['User.Read'],
    },
  };
}

function validSpaRedirectUri(value: string): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error('Microsoft Entra redirect URI is missing or invalid');
  }
  if (
    url.username !== ''
    || url.password !== ''
    || url.search !== ''
    || url.hash !== ''
    || url.pathname !== '/'
  ) {
    throw new Error('Microsoft Entra redirect URI must be an origin without credentials, path, query, or fragment');
  }
  const isLocalDevelopment = value === LOCAL_ENTRA_REDIRECT_URI;
  if (!isLocalDevelopment && url.protocol !== 'https:') {
    throw new Error('Microsoft Entra redirect URI must use HTTPS outside local development');
  }
  return value;
}

function requireUuid(value: unknown, label: string): string {
  const candidate = readString(value);
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(candidate)) {
    throw new Error(`${label} is missing or invalid`);
  }
  return candidate;
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}
